import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useMemo } from 'react';
import { CheckmarkSmIcon } from '@paypalcorp/pp-react-icons';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles, uniqueId } from '@paypalcorp/pp-react-utils';
var _excluded = ["as", "children", "className"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var IfNotEmpty = function (_ref) {
  var _ref$as = _ref.as,
    as = _ref$as === void 0 ? 'span' : _ref$as,
    children = _ref.children,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  return children ? React.createElement(as, _objectSpread({
    className: className
  }, rest), children) : null;
};
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysColorSelectedMain = tokens.sysColorSelectedMain,
    sysColorSelectedMainHover = tokens.sysColorSelectedMainHover,
    sysColorSelectedContrast = tokens.sysColorSelectedContrast,
    sysColorStructureBorderHighContrast = tokens.sysColorStructureBorderHighContrast,
    sysColorUnselectedMainHover = tokens.sysColorUnselectedMainHover,
    sysColorUnselectedMain = tokens.sysColorUnselectedMain,
    sysMotionDurationMoveSmall = tokens.sysMotionDurationMoveSmall,
    sysSizeCircleSm = tokens.sysSizeCircleSm,
    sysSpaceXs = tokens.sysSpaceXs,
    sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceXl = tokens.sysSpaceXl;
  var _textStyles = textStyles(tokens),
    textBody = _textStyles.body,
    caption = _textStyles.caption;
  var _utilStyles = utilStyles(tokens),
    focusStyles = _utilStyles.focusStyles,
    variables = _utilStyles.variables;
  var switchIndicatorWidth = '3.5rem';
  var switchBorderRadius = math("".concat(sysSizeCircleSm, " / 2"));
  var switchTranslateWidth = math("".concat(switchIndicatorWidth, " -  ").concat(sysSizeCircleSm));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: switch_base;\n    position: relative;\n    text-align: left;\n\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])));
  var label = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: label;\n    ", ";\n    display: block;\n    margin-bottom: ", ";\n\n    [dir='rtl'] & {\n      padding-right: 0;\n      padding-left: ", ";\n    }\n  "])), textBody(), sysSpaceMd, sysSpaceXl);
  var secondaryLabel = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: secondary_label;\n    display: block;\n    ", ";\n  "])), textBody({
    color: sysColorBackgroundMediumContrast
  }));
  var inline = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: inline;\n    display: flex;\n    flex-wrap: wrap;\n    align-items: center;\n    width: 100%;\n  "])));
  var inlineLabel = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: inline_label;\n    flex: 1 1 0;\n    text-align: start;\n    padding-right: ", ";\n    margin-bottom: 0;\n\n    [dir='rtl'] & {\n      padding-right: 0;\n      padding-left: ", ";\n    }\n  "])), sysSpaceXl, sysSpaceXl);
  var inlineHelperText = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: inline_helper_text;\n    flex: 0 0 100%;\n  "])));
  var container = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: container;\n    width: ", ";\n    height: ", ";\n    position: relative;\n  "])), switchIndicatorWidth, sysSizeCircleSm);
  var state = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: state;\n    display: inline-block;\n    width: ", ";\n    height: ", ";\n    cursor: pointer;\n    border: ", " solid\n      ", ";\n    border-radius: ", ";\n    background-color: ", ";\n\n    > span {\n      visibility: hidden;\n    }\n  "])), switchIndicatorWidth, sysSizeCircleSm, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, switchBorderRadius, sysColorUnselectedMain);
  var handler = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: handler;\n    position: absolute;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    pointer-events: none;\n    top: 0;\n    left: 0;\n    width: ", ";\n    height: ", ";\n    background-color: ", ";\n    border-radius: ", ";\n    border: ", " solid\n      ", ";\n    transition: ", " ", ";\n  "])), sysSizeCircleSm, sysSizeCircleSm, sysColorSelectedContrast, sysBorderRadiusInteractive, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, sysMotionDurationMoveSmall, variables.ppuiEaseMoveSmall);
  var selectedIcon = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: selected_icon;\n    display: none;\n    color: ", ";\n  "])), sysColorSelectedMain);
  var control = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: control;\n    opacity: 0;\n    position: absolute;\n\n    &:focus ~ label {\n      ", "\n    }\n\n    &:hover ~ label {\n      background-color: ", ";\n    }\n\n    &:checked ~ label {\n      background-color: ", ";\n      border: ", " solid ", ";\n    }\n\n    &:checked ~ span {\n      transform: translateX(", ");\n      border: ", " solid ", ";\n    }\n\n    &:checked + label + span > span:first-of-type {\n      display: inline-block;\n    }\n\n    &:checked:hover ~ label {\n      background-color: ", ";\n    }\n  "])), focusStyles.focusOffsetElement, sysColorUnselectedMainHover, sysColorSelectedMain, sysBorderThicknessNormal, sysColorSelectedMain, switchTranslateWidth, sysBorderThicknessNormal, sysColorSelectedMain, sysColorSelectedMainHover);
  var helperText = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: helper_text;\n    margin-top: ", ";\n    list-style: none;\n    padding-left: 0;\n    ", "\n  "])), sysSpaceXs, caption({
    color: sysColorBackgroundMediumContrast
  }));
  return {
    base: base,
    label: label,
    secondaryLabel: secondaryLabel,
    inline: inline,
    inlineLabel: inlineLabel,
    inlineHelperText: inlineHelperText,
    container: container,
    state: state,
    control: control,
    handler: handler,
    selectedIcon: selectedIcon,
    helperText: helperText
  };
}
var _excluded$1 = ["checked", "inline", "className", "inputClassName", "labelClassName", "helperText", "hideLabel", "id", "label", "secondaryLabel", "onChange", "required", "value"];
var Switch = forwardRef(function (_ref, ref) {
  var checked = _ref.checked,
    inline = _ref.inline,
    className = _ref.className,
    inputClassName = _ref.inputClassName,
    labelClassName = _ref.labelClassName,
    helperText = _ref.helperText,
    hideLabel = _ref.hideLabel,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? uniqueId('Switch_') : _ref$id,
    label = _ref.label,
    secondaryLabel = _ref.secondaryLabel,
    onChange = _ref.onChange,
    required = _ref.required,
    value = _ref.value,
    rest = _objectWithoutProperties(_ref, _excluded$1);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var containerCSSVal = [_styles.base, inline && _styles.inline];
  var labelCssVal = [_styles.label, inline && _styles.inlineLabel];
  var helperCssVal = [_styles.helperText, inline && _styles.inlineHelperText];
  var messageId = "message_".concat(id);
  var labelId = "label_".concat(id);
  var valueProps = onChange ? {
    onChange: onChange,
    checked: checked
  } : {
    defaultChecked: checked
  };
  return jsx("div", {
    css: containerCSSVal,
    className: className,
    "data-ppui-info": "switch_3.6.4"
  }, !hideLabel && jsx("span", {
    id: labelId,
    css: labelCssVal,
    "data-ppui": "true"
  }, label, jsx(IfNotEmpty, {
    as: "div",
    css: _styles.secondaryLabel,
    "data-ppui": "true"
  }, secondaryLabel)), jsx("div", {
    css: _styles.container,
    "data-ppui": "true"
  }, jsx("input", _extends({}, helperText ? {
    'aria-describedby': messageId
  } : null, !hideLabel ? {
    'aria-labelledby': labelId
  } : {}, {
    //@ts-ignore
    "aria-label": hideLabel ? label : null,
    css: _styles.control,
    className: inputClassName,
    id: id,
    role: "switch",
    type: "checkbox",
    value: value,
    ref: ref
  }, valueProps, rest, {
    "data-ppui": "true"
  })), jsx("label", {
    css: _styles.state,
    className: labelClassName,
    htmlFor: id,
    "data-ppui": "true"
  }, jsx("span", {
    "data-ppui": "true"
  }, label)), jsx("span", {
    css: _styles.handler,
    "data-ppui": "true"
  }, jsx(CheckmarkSmIcon, {
    size: "xs",
    css: _styles.selectedIcon,
    "aria-hidden": "true",
    "data-ppui": "true"
  }))), function renderMessage(help, msgId) {
    return help && jsx("div", {
      css: helperCssVal,
      id: msgId,
      "data-ppui": "true"
    }, help) || null;
  }(helperText, messageId));
});
export { Switch, styles as switchStyles };